/**
 * Common page header used across workspace pages.
 *
 * Feb 2026 R291c · Centralised density
 *   density="comfy"   (default) — existing comfortable layout.
 *   density="compact" — mid-size, keeps description visible.
 *   density="dense"   — one-row chrome; eyebrow sits inline with the
 *                       title (vertical divider between them) and the
 *                       description collapses to a native `title=`
 *                       tooltip. Reclaims ~70-90 px of header space.
 *
 * The styling for each density lives in index.css under
 * `.page-header[--comfy|--compact|--dense]` so visual tweaks don't
 * need JS changes. Tasks is the first page using "dense"; migrate
 * others one at a time.
 *
 * Backward compat: the old `compact` boolean prop still works and is
 * treated as `density="compact"`.
 */
export default function PageHeader({
  eyebrow, title, description, actions, meta, testid,
  compact = false, density,
}) {
  const resolvedDensity = density || (compact ? "compact" : "comfy");
  const classes = `page-header page-header--${resolvedDensity}`;
  if (resolvedDensity === "dense") {
    return (
      <div className={classes} data-testid={testid}>
        <div className="page-header-row">
          <div className="page-header-title" title={description || undefined}>
            {eyebrow && <span className="label-eyebrow">{eyebrow}</span>}
            <h1 className="heading-display">{title}</h1>
            {meta && (
              <div className="flex flex-wrap items-center gap-2 text-xs"
                   data-testid={testid ? `${testid}-meta` : undefined}>
                {meta}
              </div>
            )}
          </div>
          {description && (
            <span className="page-header-description sr-only">{description}</span>
          )}
          {actions && (
            <div className="page-header-actions flex items-center gap-2 flex-wrap min-w-0 justify-end">
              {actions}
            </div>
          )}
        </div>
      </div>
    );
  }
  // Comfy / compact — legacy layout retained verbatim.
  const titleCls = resolvedDensity === "compact"
    ? "heading-display text-xl sm:text-2xl md:text-3xl"
    : "heading-display text-2xl sm:text-3xl md:text-4xl";
  return (
    <div className={classes} data-testid={testid}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && <div className="label-eyebrow mb-1">{eyebrow}</div>}
          <h1 className={titleCls}>{title}</h1>
          {meta && <div className="flex flex-wrap items-center gap-2 mt-2 text-xs" data-testid={testid ? `${testid}-meta` : undefined}>{meta}</div>}
          {description && <p className="page-header-description text-muted-foreground mt-2 max-w-2xl text-sm md:text-base">{description}</p>}
        </div>
        {actions && <div className="page-header-actions flex items-start gap-2 flex-wrap min-w-0 lg:justify-end lg:max-w-[55%]">{actions}</div>}
      </div>
    </div>
  );
}
